import React, { useEffect, useState } from 'react';
import { Edit2, X, ArrowLeft, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { Movie } from '../types';

interface ManageMoviesProps {
  onClose: () => void;
}

export default function ManageMovies({ onClose }: ManageMoviesProps) {
  const [movies, setMovies] = useState<Movie[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Movie | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    description: '',
    duration: 120,
    price: 10,
  });

  useEffect(() => {
    async function fetchMovies() {
      setLoading(true);
      const { data, error } = await supabase
        .from('movies')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        toast.error('Failed to load movies');
      } else {
        setMovies(data || []);
      }
      setLoading(false);
    }
    fetchMovies();
  }, []);

  const startEdit = (movie: Movie) => {
    setEditing(movie);
    setFormData({
      title: movie.title,
      image_url: movie.image_url,
      description: movie.description,
      duration: movie.duration,
      price: movie.price,
    });
  };

  const deleteMovie = async (movie: Movie) => {
    const first = window.confirm('You selected to delete this movie. Click OK to continue.');
    if (!first) return;
    const second = window.confirm('This will permanently delete the movie and all related data. Click OK to confirm.');
    if (!second) return;
    const { error } = await supabase
      .from('movies')
      .delete()
      .eq('id', movie.id);
    if (error) {
      toast.error('Failed to delete movie');
      return;
    }
    toast.success('Movie deleted');
    setMovies(prev => prev.filter(m => m.id !== movie.id));
    if (editing && editing.id === movie.id) {
      setEditing(null);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
    }));
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!formData.title || !formData.image_url || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (formData.duration <= 0) {
      toast.error('Duration must be greater than 0');
      return;
    }
    if (formData.price <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }
    const { data, error } = await supabase
      .from('movies')
      .update({
        title: formData.title,
        image_url: formData.image_url,
        description: formData.description,
        duration: formData.duration,
        price: formData.price,
      })
      .eq('id', editing.id)
      .select();
    if (error) {
      toast.error('Failed to update movie');
      return;
    }
    toast.success('Movie updated');
    const updated = data?.[0];
    setMovies(prev => prev.map(m => (m.id === updated.id ? updated : m)));
    setEditing(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center pt-20">
      <div className="bg-gray-800 rounded-lg shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h2 className="text-2xl font-bold text-white">Edit Movies</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <p className="text-gray-300">Loading...</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {movies.map(movie => (
                <div key={movie.id} className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white font-semibold">{movie.title}</h3>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => startEdit(movie)}
                        className="text-indigo-300 hover:text-indigo-200"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteMovie(movie)}
                        className="text-red-300 hover:text-red-200"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-300 mt-2 line-clamp-3">{movie.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {editing && (
          <div className="border-t border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Editing: {editing.title}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Image URL *</label>
                <input
                  type="text"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Duration (min) *</label>
                  <input
                    type="number"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    min={1}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Price (€) *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    step="0.01"
                    min={0.01}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveEdit}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
